import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, Button, FormControlLabel, Checkbox } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import BasicDialog from '../../base/BasicDialog';
import axios from '../../../api/axios';
import { useSnackbarAlert } from '../../../context/snackbarAlert';
import { useForm } from '../../../hooks/useForm';
import useCorrespondenceAdminService from '../../../services/correspondenceAdminService';
import { useAsync, useAsyncFn } from '../../../hooks/useAsync';
import { isEmptyValue } from '../../../helpers/methods';
import TextFieldFormControl from '../../field/TextFieldFormControl';
import CheckboxField from '../../base/CheckboxField/checkboxField';

const CreatePostDeliveryDialog = (props) => {
  const { t } = useTranslation();
  const { formValue, setFormValue, onChange } = useForm({ delivery_active: true });

  const { createPostDelivery, updatePostDeliveryData, getPostDeliveryDetails } = useCorrespondenceAdminService()
  const createPostDeliveryFn = useAsyncFn(createPostDelivery);
  const updatePostDeliveryFn = useAsyncFn(updatePostDeliveryData);
  const snackbarAlert = useSnackbarAlert();

  const postDeliveryDetailsData = useAsync(() => {
    if (props.deliveryId) {
      return getPostDeliveryDetails(props.deliveryId);
    }
    return Promise.resolve({});
  }, [props.postDeliveryId]);


  useEffect(() => {
    if (!postDeliveryDetailsData.loading && postDeliveryDetailsData.value && props.deliveryId) {
      setFormValue(postDeliveryDetailsData.value);
    }
  }, [postDeliveryDetailsData.loading]);


  const onChangeCheckbox = useCallback((e) => {
    const { name, checked } = e.target;
    setFormValue((prev) => ({ ...prev, [name]: checked }));
  }, []);

  const onSubmit = (postDeliveryData) => {
    if (postDeliveryData.id) {
      updatePostDeliveryFn.execute(postDeliveryData.id, postDeliveryData)
        .then(() => {
          snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.post_delivery_updated"));
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          props.onClose(true);
        })
        .catch(() => {
          snackbarAlert.openErrorSnackbarAlert(t("snackbar_alert.occurred_error_during_updating_post_delivery"));
        });
    } else {
      createPostDeliveryFn.execute(postDeliveryData)
        .then(() => {
          snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.post_delivery_created"));
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          props.onClose(true);
        })
        .catch(() => {
          snackbarAlert.openErrorSnackbarAlert(t("snackbar_alert.occurred_error_during_creating_new_post_delivery"));
        });
    }
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      onSubmit(formValue);
    }
  };

  const isFormValid = () => {
    return !isEmptyValue(formValue.delivery_name);
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      maxWidth="md"
      title={props.deliveryId ? t('dialog.correspondence_admin_panel_dialogs.edit_post_delivery') : t('dialog.correspondence_admin_panel_dialogs.add_post_delivery')}
    >
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextFieldFormControl
            name="delivery_name"
            label={t('dialog.correspondence_admin_panel_dialogs.delivery_name')}
            value={formValue["delivery_name"]}
            onChange={onChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextFieldFormControl
            name="delivery_cost"
            label={t('dialog.correspondence_admin_panel_dialogs.delivery_cost')}
            type="number"
            value={formValue["delivery_cost"]}
            onChange={onChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <CheckboxField
            label={t('dialog.correspondence_admin_panel_dialogs.active')}
            name="delivery_active"
            checked={formValue["delivery_active"]}
            onChange={onChangeCheckbox}
          />
        </Grid>
        <Grid item xs={6}>
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            {props.deliveryId ? t('save') : t('add')}
          </LoadingButton>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={props.onClose}
          >
            {t('cancel')}
          </Button>
        </Grid>
      </Grid>
    </BasicDialog>
  );
};

CreatePostDeliveryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  deliveryId: PropTypes.string,
  onRefetchData: PropTypes.func.isRequired
};

CreatePostDeliveryDialog.defaultProps = {
  deliveryId: null
};

export default CreatePostDeliveryDialog;
